<template>
	<form-select v-bind="props"
				 v-model="submitValue"
				 v-model:filter="filterValue"
				 @update:createValue="value => emit('update:createValue', value)"
				 :options="options"
				 :autofocus="props.autofocus"
				 :skeletons="!props.data?.limit || !isNaN(props.data?.limit) ? props.data?.limit : undefined"
				 :filter-function="props.remote ? search : null"
				 :loading="isLoading"
				 ref="selectComponent">
		<template #below_options="bindings">
			<slot name="below_options" v-bind="bindings"/>
		</template>
	</form-select>
</template>

<script setup lang="ts">
import FormSelect from "./FormSelect";
import {deepEqual, getPropValue} from "@spoferan/spoferan-ts-core";
import {computed, onMounted, ref, watch, defineModel} from 'vue';
import type {Ref} from 'vue';
import {useNuxtApp} from '#app';

const {$apiFetch} = useNuxtApp();

const props = withDefaults(defineProps<{

	// The value of the select
	modelValue: any,

	// -- below are select specific

	// Whether custom values are allowed
	allowCreate?: boolean,

	// The value to use to store a new created option value
	createValue?: string,

	// -- below are remote select specific

	// The url where to load the options from
	action: string,

	// The additional data to send to the server for fetching the options
	data?: { [key: string]: any },

	// The property name of the results where to retrieve the option's display text from
	textProp?: string,

	// The property name of the results where to retrieve the option's value from
	valueProp?: string,

	// Whether to remote search the options when typing
	remote?: boolean,

	// Whether to fetch the options as a guest even though a user is authenticated to improve caching performance
	asGuest?: boolean,

	// Whether the options can be filtered
	filterable?: boolean,

	// Whether the options menu shall be opened as soon as the component is rendered
	autofocus?: boolean,

	// The url where to load the initial option from when it's not included in the current options set.
	// If unspecified the 'action' param will be used instead
	// Requires the current submitValue to be appended at the end of the url
	// Example: 'initialOptionAction' set to '/v1/resources' will load from '/v1/resources/{submitValue}' (if submitValue is not already appended)
	initialOptionAction?: string,

	// The initial option to always show on the dropdown
	initialOption?: { [key: string]: any },

	// A function to build each individual option item
	optionBuilder?: (option: { [key: string]: any }) => { [key: string]: any },

	// Additional options to select
	options?: { [key: string]: any }[],
}>(), {
	textProp: 'title',
	valueProp: 'id',
	remote: true,
	filterable: true,
	options: []
});

const emit = defineEmits([
	'update:modelValue',
	'update:createValue',
	'loaded' // Emitted when the initial options are first loaded
]);

// The input value to filter the options for selection
const filterValue: Ref<string | null | undefined> = defineModel('filter');

const submitValue = ref(props.modelValue);
watch(submitValue, (newValue) => {
	emit('update:modelValue', newValue);

	// If a new filtered option is selected that is not included in the plain loaded items, we need to add the filtered option to the loaded items to have the selected
	// option persisted even though the user continues filtering the select on multi selects
	const newValues = Array.isArray(newValue) ? newValue : [newValue];
	newValues.map(value => {
		const filteredItemValue = filteredItems.value.find(item => getPropValue(item, props.valueProp) === value);
		if (filteredItemValue && !items.value.find(item => getPropValue(item, props.valueProp) === value)) {
			items.value.splice(0, 0, filteredItemValue);
		}
	});
}, {deep: true});
watch(() => props.modelValue, (newValue) => {
	submitValue.value = newValue;
});

const isLoading = ref(false);

// The plain loaded options from the server
const items = ref([]);

// The last plain loaded filtered options from the server
// Required to prevent loading missing options when filtered option is selected
const filteredItems = ref([]);

const selectComponent = ref(null);

// The non-changeable representation of the component's initial value
const initialValue = props.modelValue;

// The loaded initial option to always show on the dropdown
let loadedInitialOption = props.initialOption;

const options = computed(() => {
	const fullOptions = [...props.options, ...items.value];
	return fullOptions.map(item => {
		let props = {...item};
		if (props.optionBuilder) {
			props = props.optionBuilder(item);
		}

		return props;
	})
});

watch(() => props.action, () => {
	refresh();
});

watch(() => props.data, (newValue, oldValue) => {
	if (!deepEqual(newValue, oldValue)) {
		refresh();
	}
}, {deep: true});

// When the value changes externally, we potentially need to load missing options for the value
watch(() => props.modelValue, (value) => {
	if (value) {
		loadMissingValueItems(value);
	}
});

watch(() => props.initialOption, (initialOption) => {
	// Add the initial option if it does not exist since we need it to display the initial value on the select
	if (initialOption && !items.value.find(item => getPropValue(props.valueProp, item) === getPropValue(props.valueProp, initialOption))) {
		items.value.splice(0, 0, initialOption);
	}

	if (initialOption) {
		loadedInitialOption = initialOption;
	}
});

async function load(additionalFilter: {[key: string]: any} = {}) {
	isLoading.value = true;
	const params = Object.assign({...props.data}, {...additionalFilter});
	const {data} = await $apiFetch(props.action, {params, guest: props.asGuest});

	isLoading.value = false;

	return data;
}

async function refresh() {
	items.value = await load();

	// Add the initial option if it does not exist since we need it to display the initial value on the select
	if (loadedInitialOption) {
		if (!items.value.find(item => getPropValue(item, props.valueProp) === getPropValue(loadedInitialOption, props.valueProp))) {
			items.value.splice(0, 0, loadedInitialOption);
		}
	} else if (initialValue) {
		await loadMissingValueItems(initialValue);
	}

	// Initialize the selected value
	emit('loaded', items.value)
}

async function loadMissingValueItems(value) {
	const optionValues = Array.isArray(value) ? value : [value];
	const params = {...props.data};

	const requests = [];
	optionValues.map(optionValue => {
		// If we have a value that wasn't loaded as an option, we have to load it to display the selected value
		if (!items.value.find(item => getPropValue(item, props.valueProp) === optionValue)) {

			// The value might have already been loaded by filtering, so we have to check that first
			const filteredItemValue = filteredItems.value.find(item => getPropValue(item, props.valueProp) === optionValue);
			if (filteredItemValue) {
				items.value.splice(0, 0, filteredItemValue);
			} else {
				const initialOptionApiPath: string = props.initialOptionAction ?? props.action;
				const initialOptionAction = initialOptionApiPath.endsWith(optionValue) ? initialOptionApiPath : `${initialOptionApiPath}/${optionValue}`;

				requests.push($apiFetch(initialOptionAction, {params, guest: props.asGuest}).catch((error) => {
					console.error(`Could not load missing value as select option from [${initialOptionAction}].`, error);
				}))
			}
		}
	});

	const responses = await Promise.all(requests);
	responses.forEach(response => {
		if (response?.data) {
			items.value.splice(0, 0, response.data);
		}
	})
}

function clearValue() {
	selectComponent.value.clearValue();
}

async function search(query) {
	filteredItems.value = await load({search: query});
	return filteredItems.value;
}

onMounted(() => {
	refresh();
});

defineExpose({
	refresh,
	clearValue,
});
</script>